import * as AppConstants from "AppConstants";

const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];
const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Turns any date-ish value into a Date positioned at local midnight of the calendar day it names.
 *
 * new Date() picks its timezone from the *shape* of what it is given: "2026-07-29" is parsed as UTC
 * midnight, while "2026-07-29T00:00:00", (2026, 6, 29) and an existing Date are all local. Every
 * getter (getFullYear, getMonth, getDate, getDay) then reads in local time. For the date-only
 * string form those two disagree by the browser's offset, so the day read back out is not always
 * the day that went in — one day early for anyone west of UTC.
 *
 * Building from the Y/M/D parts keeps parse and read in the same timezone, so a calendar day
 * survives the round trip regardless of where the user is.
 *
 * @since NEXT_VERSION
 */
export const parseLocalDate = (value) => {
    if (typeof value === "string") {
        const parts = DATE_ONLY.exec(value.trim());
        if (parts) {
            return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
        }
    }
    return new Date(value);
};

/**
 * Local midnight of the day a value falls on — the calendar day with the time-of-day discarded.
 *
 * Use this for any "is this date in the past / future / today" question. Comparing a date against
 * Date.now() directly answers a question about *instants*, which is not the same question: for a
 * UTC+X user, today's date-only string is an instant X hours in the future, so today reads as
 * future until UTC catches up (00:00–05:30 daily in IST).
 *
 * @since NEXT_VERSION
 */
export const startOfLocalDay = (value) => {
    const dt = parseLocalDate(value);
    return new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
};

/**
 * Local midnight of the last day an activity may be dated: today plus the allowed lead days, so a
 * timesheet can be filled in before the period it covers has ended. Lives here rather than beside
 * the activity validator because the period filters bound their date pickers by it too.
 *
 * @since NEXT_VERSION
 */
export const getLatestActivityDate = () => {
    const today = startOfLocalDay(new Date());
    return new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() + AppConstants.ACTIVITY_FUTURE_DAYS_ALLOWED
    );
};

/**
 * Local midnight of the earliest day an activity may be dated: today less the allowed backdating
 * window. Anything older belongs to a timesheet that has already been reported on, so it is fixed
 * by correcting the saved activity rather than by logging a new one.
 *
 * The day-of-month is clamped to the target month's last day. Letting the Date constructor roll an
 * overflow forward (31 May less 3 months has no 31 February) would quietly shorten the window by a
 * few days in exactly the months a user is most likely to be catching up in.
 *
 * @since NEXT_VERSION
 */
export const getEarliestActivityDate = () => {
    const today = startOfLocalDay(new Date());
    const target = new Date(
        today.getFullYear(),
        today.getMonth() - AppConstants.ACTIVITY_PAST_MONTHS_ALLOWED,
        1
    );
    const lastDayOfTarget = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    return new Date(
        target.getFullYear(),
        target.getMonth(),
        Math.min(today.getDate(), lastDayOfTarget)
    );
};

/**
 * Returns date in YYYY-MM-DD format
 * */
export const getDateInFormat = (dt) => {
    dt = parseLocalDate(dt);
    const toReturn = `${dt.getFullYear()}-${(dt.getMonth()+1).toString().padStart(2, "0")}-${dt.getDate().toString().padStart(2, "0")}`;
    return toReturn;
};

/**
 * Returns Months Array
 * */
export const getAllMonths = () => {
    return monthNames.map((month, index) => {
        return {
            value: index,
            label: month
        }
    });
}

/*
 * Returns date in DD-MM-YYYY format 
 * */
export const getDateInDDMMYYYYFormat = (dt) => {
    dt = parseLocalDate(dt);
    const toReturn = `${dt.getDate().toString().padStart(2, "0")}-${(dt.getMonth()+1).toString().padStart(2, "0")}-${dt.getFullYear()}`;
    return toReturn;
}

/**
 * Get current month start date
 */

export const getStartDateFromWeekNumber = (w, y) => {
    let date = new Date(y, 0, (1 + (w - 1) * 7)); // Elle's method
    date.setDate(date.getDate() + (1 - date.getDay())); // 0 - Sunday, 1 - Monday etc
    return date
}

export const getEndDateFromWeekNumber = (w, y) => {
    let date = new Date(y, 0, (1 + (w - 1) * 7)); // Elle's method
    date = new Date(date.setDate(date.getDate() - date.getDay() + 7));
    return date
}

export const getStartDateFromAnyMonth = (m, y) => {
    return new Date(y, m, 1)
}

export const getEndDateFromAnyMonth = (m, y) => {
    var today = new Date(y, m, 1);
    return new Date(today.getFullYear(), today.getMonth() + 1, 0);

}

export const getCurrentMonthStartDate = () => {
    var dt = new Date();
    dt.setDate(dt.getDate() - dt.getDate() + 1);
    return getDateInFormat(dt);
}

export const getCurrentYearStartDate = () => {
    var dt = new Date((new Date()).getFullYear(), 0, 1);
    return getDateInFormat(dt);
}

export const getCurrentYearEndDate = () => {
    var dt = new Date((new Date()).getFullYear(), 11, 31);
    return getDateInFormat(dt);
}

export const getMonthName = (monthNum) => {
    if (monthNum < 1 || monthNum > 12) return '';
    return monthNames[monthNum - 1];

}

export const getDayName = (date) => {
    var dt = parseLocalDate(date);
    return dayNames[dt.getDay()];
}

/*
 * These parse both operands the same way, so two date-only strings compare correctly whatever the
 * browser offset — the offsets cancel. That is only true while the operands are the same kind: a
 * date-only string against a Date or a live instant mixes UTC-parsed with local, and the comparison
 * shifts by the offset. parseLocalDate removes the asymmetry at the source rather than relying on
 * every caller to pass matching types.
 *
 * For a "has this day arrived" question prefer startOfLocalDay on both sides, which also discards
 * the time of day.
 */
export const isDateGreaterThan = (date1, date2) => {
    try {
        var dt1 = parseLocalDate(date1);
        var dt2 = parseLocalDate(date2);
        return dt1 > dt2;
    } catch (err) {
        return false;
    }
}

export const isDateGreaterThanEquals = (date1, date2) => {
    try {
        var dt1 = parseLocalDate(date1);
        var dt2 = parseLocalDate(date2);
        return dt1 >= dt2;
    } catch (err) {
        return false;
    }
}

export const isDateLessThan = (date1, date2) => {
    try {
        var dt1 = parseLocalDate(date1);
        var dt2 = parseLocalDate(date2);
        return dt1 < dt2;
    } catch (err) {
        return false;
    }
}

export const isDateLessThanEquals = (date1, date2) => {
    try {
        var dt1 = parseLocalDate(date1);
        var dt2 = parseLocalDate(date2);
        return dt1 <= dt2;
    } catch (err) {
        return false;
    }
}

export const workingDaysBetweenDates = (d0, d1) => {
    // No holidays involved in the calculation
    return workingDaysBetweenDatesExceptHolidays(d0, d1, []);
}

export const workingDaysBetweenDatesExceptHolidays = (d0, d1, holidays) => {
    var startDate = new Date(d0);
    var endDate = new Date(d1);
    // populate the holidays array with all required dates without first taking care of what day of the week they happen
    // var holidays = ['2018-12-09', '2018-12-10', '2018-12-24', '2018-12-31'];
    // Validate input
    if (endDate < startDate)
        return 0;

    var z = 0; // number of days to substract at the very end
    for (var i = 0; i < holidays.length; i++) {
        var cand = parseDate(holidays[i]);
        var candDay = cand.getDay();

        if (cand >= startDate && cand <= endDate && candDay !== 0 && candDay !== 6) {
            // we'll only substract the date if it is between the start or end dates AND it isn't already a saturday or sunday
            z++;
        }

    }
    // Calculate days between dates
    var millisecondsPerDay = 86400 * 1000; // Day in milliseconds
    startDate.setHours(0, 0, 0, 1); // Start just after midnight
    endDate.setHours(23, 59, 59, 999); // End just before midnight
    var diff = endDate - startDate; // Milliseconds between datetime objects    
    var days = Math.ceil(diff / millisecondsPerDay);

    // Subtract two weekend days for every week in between
    var weeks = Math.floor(days / 7);
    days = days - (weeks * 2);

    // Handle special cases
    var startDay = startDate.getDay();
    var endDay = endDate.getDay();

    // Remove weekend not previously removed.   
    if (startDay - endDay > 1)
        days = days - 2;

    // Remove start day if span starts on Sunday but ends before Saturday
    if (startDay === 0 && endDay !== 6)
        days = days - 1

    // Remove end day if span ends on Saturday but starts after Sunday
    if (endDay === 6 && startDay !== 0)
        days = days - 1

    // substract the holiday dates from the original calculation and return to the DOM
    return days - z;
}

function parseDate(input) {
    // Transform date from text to date
    var parts = input.match(/(\d+)/g);
    // new Date(year, month [, date [, hours[, minutes[, seconds[, ms]]]]])
    return new Date(parts[0], parts[1] - 1, parts[2]); // months are 0-based
}